import { useMutation, useQuery } from "react-query";
import { deleteUser, fetchUserSettings, getActionPinValidity, getUser, getUsers, patchUser, patchUserLanguage, postClearUserTokens, postGenerateUserPassword, postUser } from "./requests";
import { notifyError, notifySuccess } from "@/toast-notifications";
import queryClient from "../queryClient";
import useReduxStore from "@/hooks/useReduxStore";
import { updateUserSettingsSuccess } from "@/store/actions";
import { GET_USER_SETTINGS_ERROR } from "@/errors";

export const GET_ACTION_PIN_VALIDITY_KEY = "get-action-pin-validity";
export const useGetActionPinValidity = (actionPin, options = {}) =>
    useQuery(
        [GET_ACTION_PIN_VALIDITY_KEY, actionPin],
        () => getActionPinValidity(actionPin),
        {
            ...options,
            staleTime: 0,
            cacheTime: 0,
        },
    );

export const USERS_KEY = "users";

export const useGetUsers = (options = {}) =>
    useQuery({
        ...options,
        queryKey: [USERS_KEY],
        queryFn: getUsers,
        onError: () => {
            notifyError("queries:users.error.get_other");
        },
    });

export const useGetUser = (id, options = {}) =>
    useQuery({
        ...options,
        queryKey: [USERS_KEY, id],
        queryFn: () => getUser(id),
        onError: () => {
            notifyError("queries:users.error.get_one");
        },
    });

export const usePostUser = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: postUser,
        onSuccess: user => {
            queryClient.setQueryData(
                [USERS_KEY],
                list => [...list, user],
            );

            queryClient.setQueryData(
                [USERS_KEY, user?.id],
                user,
            );

            notifySuccess("queries:users.success.post");
        },
        onError: () => {
            notifyError("queries:users.error.post");
        },
    });

export const usePatchUser = (options = {}) => {

    const { slice: currentUser, dispatch } = useReduxStore(state => state.authenticationReducer.userSettings);

    return useMutation({
        ...options,
        mutationFn: patchUser,
        onSuccess: user => {
            queryClient.setQueryData(
                [USERS_KEY],
                list => list.map(u => u.id === user.id ? user : u),
            );

            queryClient.setQueryData(
                [USERS_KEY, user?.id],
                user,
            );

            // Legacy adaptation            
            if (currentUser?.id === user.id)
                dispatch(updateUserSettingsSuccess(user));

            notifySuccess("queries:users.success.patch");
        },
        onError: () => {
            notifyError("queries:users.error.patch");
        },
    });
}

export const useDeleteUser = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: deleteUser,
        onSuccess: user => {
            queryClient.setQueryData(
                [USERS_KEY],
                list => list.filter(u => u.id !== user.id),
            );

            queryClient.removeQueries({
                queryKey: [USERS_KEY, user?.id],
            });

            notifySuccess("queries:users.success.delete");
        },
        onError: () => {
            notifyError("queries:users.error.delete");
        },
    });

export const usePostClearUserTokens = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: postClearUserTokens,
        onSuccess: () => {
            notifySuccess("queries:users.success.clear_tokens");
        },
        onError: () => {
            notifyError("queries:users.error.clear_tokens");
        },
    });

export const usePostGenerateUserPassword = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: postGenerateUserPassword,
        onSuccess: () => {
            notifySuccess("queries:users.success.generate_password");
        },
        onError: () => {
            notifyError("queries:users.error.generate_password");
        },
    });

export const USER_SETTINGS_KEY = "user-settings";

export function useGetUserSettings(options = {}) {
    return useQuery(
        [USER_SETTINGS_KEY],
        fetchUserSettings,
        {
            onError: () => {
                notifyError(GET_USER_SETTINGS_ERROR);
            },
            ...options,
        }
    );
}

export function usePatchUserLanguage(options = {}) {
    return useMutation({
        ...options,
        mutationFn: patchUserLanguage,
        onSuccess: (data, language) => {
            notifySuccess("queries:users.success.patch_language");
            queryClient.setQueryData([USER_SETTINGS_KEY], (old) => ({
                ...old,
                language, 
            }));
        },
        onError: () => {
            notifyError("queries:users.error.patch_language");
        },
    });
}



// WEBPACK FOOTER //
// ./src/queries/users/hooks.js